<template>
  <section class="news-page page-stack">
    <article class="card page-hero-card news-hero">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Warcraft news</p>
          <h2>News</h2>
          <p>Official blue posts, Wowhead, and Noob Club in one focused Warcraft reader.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar news-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Posts: <span>{{ totalPostCount }}</span></span>
        <span class="filter-pill">Feed: <span>{{ activeFeed.label }}</span></span>
        <span v-if="totalPages > 1" class="filter-pill">Page: <span>{{ currentPage }} / {{ totalPages }}</span></span>
        <span v-if="lastUpdatedLabel" class="filter-pill">Updated: <span>{{ lastUpdatedLabel }}</span></span>
        <span v-if="search" class="filter-pill">Search: <span>{{ search }}</span></span>
      </div>
      <div role="group" class="quick-search-field">
        <input
          v-model.trim="search"
          class="input"
          :aria-label="`Search ${activeFeed.label}`"
          :placeholder="`Search ${activeFeed.label}`"
          type="search"
        />
      </div>
      <div class="page-actions">
        <div v-if="!isNativeRussianFeed" class="language-dropdown" :class="{ 'is-open': languageMenuOpen }" aria-label="News language">
          <button
            class="btn-sm-outline language-dropdown-trigger"
            type="button"
            :disabled="loading"
            aria-haspopup="menu"
            :aria-expanded="languageMenuOpen"
            @click.stop="languageMenuOpen = !languageMenuOpen"
          >
            <i class="fa-solid fa-language"></i>
            {{ activeLanguageLabel }}
            <i class="fa-solid fa-chevron-down"></i>
          </button>
          <Transition name="favorite-menu">
            <div v-if="languageMenuOpen" class="language-dropdown-menu" role="menu" @click.stop>
              <button
                v-for="language in languages"
                :key="language.value"
                type="button"
                role="menuitem"
                :class="{ 'is-active': activeLanguage === language.value }"
                @click="selectLanguage(language.value)"
              >
                <span>{{ language.name }}</span>
                <i v-if="activeLanguage === language.value" class="fa-solid fa-check"></i>
              </button>
            </div>
          </Transition>
        </div>
        <span v-if="!isNativeRussianFeed" class="translation-provider-chip">
          <i class="fa-solid fa-bolt"></i>
          {{ translationProviderLabel }}
        </span>
        <div class="view-switch news-layout-switch" aria-label="News layout">
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': viewMode === 'grid' }"
            type="button"
            aria-label="Grid layout"
            title="Grid layout"
            @click="viewMode = 'grid'"
          >
            <i class="fa-solid fa-grip"></i>
          </button>
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': viewMode === 'list' }"
            type="button"
            aria-label="List layout"
            title="List layout"
            @click="viewMode = 'list'"
          >
            <i class="fa-solid fa-list"></i>
          </button>
        </div>
        <button class="btn-icon-outline" type="button" aria-label="Refresh news" title="Refresh news" :disabled="loading" @click="loadPosts(true)">
          <i class="fa-solid fa-rotate"></i>
        </button>
      </div>
    </div>

    <div class="card wowhead-feed-tabs news-source-tabs" aria-label="News sources">
      <button
        v-for="source in sourceTabs"
        :key="source.key"
        class="btn-sm-outline"
        :class="{ 'is-active': activeSource === source.key }"
        type="button"
        @click="selectSource(source.key)"
      >
        {{ source.label }}
      </button>
    </div>

    <div v-if="activeSource === 'wowhead'" class="card wowhead-feed-tabs news-category-tabs" aria-label="Wowhead categories">
      <button
        v-for="category in wowheadCategories"
        :key="category.key"
        class="btn-sm-outline"
        :class="{ 'is-active': wowheadCategory === category.key }"
        type="button"
        @click="selectFeed(category.key)"
      >
        {{ category.label }}
      </button>
    </div>

    <div v-if="isNativeRussianFeed" class="card source-language-note" role="status">
      <span><strong>Noob Club publishes in Russian.</strong> These posts are shown in their original language and are not translated into Russian again.</span>
    </div>

    <div v-if="loading" class="news-grid" :class="{ 'is-list-view': viewMode === 'list' }">
      <article v-for="index in 6" :key="index" class="card news-card news-skeleton">
        <span class="skeleton-media news-banner-skeleton"></span>
        <span class="skeleton-line skeleton-line-short"></span>
        <span class="skeleton-line"></span>
        <span class="skeleton-line skeleton-line-medium"></span>
      </article>
    </div>

    <AppEmptyState
      v-else-if="loadError"
      eyebrow="News"
      icon="fa-solid fa-triangle-exclamation"
      title="Unable to load the feed"
      :message="loadError"
    />

    <AppEmptyState
      v-else-if="!currentPageSourcePosts.length"
      eyebrow="No matches"
      icon="fa-solid fa-magnifying-glass"
      title="No posts found"
      message="Try a different search phrase, switch feed source, or refresh the feed."
    />

    <template v-else>
      <div class="news-grid" :class="{ 'is-list-view': viewMode === 'list' }">
        <article
          v-for="post in posts"
          :key="post.id"
          class="card news-card"
          :class="{ 'is-news-card': post.kind === 'news', 'is-blue-card': post.kind === 'blue', 'has-banner': post.kind !== 'blue' }"
        >
          <div class="wowhead-card-banner" :class="{ 'is-blue-banner': post.kind === 'blue', 'is-empty-banner': !post.imageUrl && post.kind !== 'blue' }">
            <RouterLink v-if="post.imageUrl" class="wowhead-news-banner" :to="articleRoute(post)">
              <img :src="post.imageUrl" :alt="post.title" loading="lazy" @error="handleCardImageError(post)">
            </RouterLink>
            <div v-else-if="post.kind === 'blue'" class="wowhead-blue-banner" aria-hidden="true">
              <i class="fa-brands fa-battle-net"></i>
            </div>
            <RouterLink v-else class="wowhead-news-banner wowhead-news-banner-fallback" :to="articleRoute(post)">
              <i class="fa-solid fa-newspaper"></i>
              <span>{{ post.sourceLabel || 'News' }}</span>
              <strong>{{ post.title }}</strong>
            </RouterLink>
            <span class="wowhead-category-badge">{{ post.category }}</span>
          </div>

          <header class="news-card-head">
            <div>
              <h3>
                <RouterLink :to="articleRoute(post)">{{ post.title }}</RouterLink>
              </h3>
            </div>
          </header>

          <p class="news-card-summary">{{ post.summary || `Open the ${post.sourceLabel || 'source'} post to read the full update.` }}</p>

          <footer class="news-card-foot">
            <time v-if="post.publishedAt" :datetime="post.publishedAt">{{ formatDate(post.publishedAt) }}</time>
            <span v-else>Recent update</span>
            <RouterLink class="ranking-link" :to="articleRoute(post)">
              Read post
              <i class="fa-solid fa-arrow-right"></i>
            </RouterLink>
          </footer>
        </article>
      </div>

      <AppPagination
        v-if="totalPages > 1"
        :page="currentPage"
        :total-pages="totalPages"
        aria-label="News pagination"
        page-text="Page"
        @change="changePage"
      />
    </template>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import AppPagination from '../components/AppPagination.vue'
import { WOWHEAD_NEWS_FEEDS, getFeedFallbackStatus, getWowheadArticleImage, getWowheadFeedPostsRaw } from '../services/wowheadNews'
import { getLastTranslationProvider, translatePosts } from '../services/translation'

const NEWS_STATE_KEY = 'rio-dashboard-news-state'
const NEWS_LANGUAGE_KEY = 'rio-news-language'
const NEWS_RETURN_STATE_KEY = 'rio-news-return-state'
const NEWS_PAGE_SIZE = 24
const NEWS_FEED_KEYS = ['all', 'blue', 'retail', 'classic', 'noob-club']
const WOWHEAD_CATEGORY_KEYS = ['all', 'retail', 'classic']
const DEFAULT_NEWS_STATE = {
  viewMode: 'grid',
  activeLanguage: 'en',
  activeFeedKey: 'all',
}

function normalizeFeedKey(value) {
  const feedKey = Array.isArray(value) ? value[0] : value
  return NEWS_FEED_KEYS.includes(feedKey) ? feedKey : null
}

function routeValue(value) {
  return Array.isArray(value) ? value[0] : value
}

function resolveRouteFeedKey(query = {}) {
  const source = routeValue(query.source)

  if (source === 'wowhead') return normalizeFeedKey(query.category) || 'all'
  if (source === 'blue') return 'blue'
  if (source === 'noob-club') return 'noob-club'

  // Keep links shared before the source/category navigation update working.
  return normalizeFeedKey(query.feed)
}

function loadNewsState() {
  try {
    const saved = JSON.parse(localStorage.getItem(NEWS_STATE_KEY) || '{}')
    return {
      viewMode: ['grid', 'list'].includes(saved.viewMode) ? saved.viewMode : DEFAULT_NEWS_STATE.viewMode,
      activeLanguage: ['en', 'uk', 'ru'].includes(localStorage.getItem(NEWS_LANGUAGE_KEY)) ? localStorage.getItem(NEWS_LANGUAGE_KEY) : DEFAULT_NEWS_STATE.activeLanguage,
      activeFeedKey: normalizeFeedKey(saved.activeFeedKey) || DEFAULT_NEWS_STATE.activeFeedKey,
    }
  } catch (error) {
    return { ...DEFAULT_NEWS_STATE }
  }
}

function loadNewsReturnState() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(NEWS_RETURN_STATE_KEY) || 'null')
    if (!saved || !normalizeFeedKey(saved.activeFeedKey)) return null

    return {
      activeFeedKey: saved.activeFeedKey,
      currentPage: Math.max(1, Number(saved.currentPage) || 1),
      search: String(saved.search || ''),
      scrollTop: Math.max(0, Number(saved.scrollTop) || 0),
    }
  } catch {
    return null
  }
}

export default {
  name: 'NewsView',
  components: {
    AppEmptyState,
    AppPagination,
  },
  data() {
    const savedState = loadNewsState()
    const returnState = loadNewsReturnState()
    const sharedFeedKey = resolveRouteFeedKey(this.$route.query)

    return {
      posts: [],
      rawPosts: [],
      search: returnState?.search || '',
      loading: false,
      loadError: '',
      lastUpdatedAt: '',
      activeFeedKey: sharedFeedKey || returnState?.activeFeedKey || savedState.activeFeedKey,
      currentPage: returnState?.currentPage || 1,
      pendingScrollTop: returnState?.scrollTop ?? null,
      pageSize: NEWS_PAGE_SIZE,
      viewMode: savedState.viewMode,
      activeLanguage: savedState.activeLanguage,
      languageMenuOpen: false,
      translationInProgress: false,
      translationProvider: getLastTranslationProvider(),
      languages: [
        { value: 'en', label: 'EN', name: 'English' },
        { value: 'uk', label: 'UA', name: 'Українська' },
        { value: 'ru', label: 'RU', name: 'Русский' },
      ],
      feeds: WOWHEAD_NEWS_FEEDS,
    }
  },
  computed: {
    activeSource() {
      if (this.activeFeedKey === 'blue') return 'blue'
      if (this.activeFeedKey === 'noob-club') return 'noob-club'
      return 'wowhead'
    },
    wowheadCategory() {
      return WOWHEAD_CATEGORY_KEYS.includes(this.activeFeedKey) ? this.activeFeedKey : 'all'
    },
    sourceTabs() {
      return [
        { key: 'wowhead', label: 'Wowhead' },
        { key: 'blue', label: 'Blue Tracker' },
        { key: 'noob-club', label: 'Noob Club' },
      ]
    },
    wowheadCategories() {
      return [
        { key: 'all', label: 'All News' },
        { key: 'retail', label: 'Retail WoW' },
        { key: 'classic', label: 'Classic WoW' },
      ]
    },
    activeFeed() {
      return this.feeds.find((feed) => feed.key === this.activeFeedKey) || this.feeds[0]
    },
    rssUrl() {
      return this.activeFeed.url
    },
    isNativeRussianFeed() {
      return this.activeFeed.nativeLanguage === 'ru'
    },
    filteredRawPosts() {
      const query = this.search.toLowerCase()
      if (!query) return this.rawPosts

      return this.rawPosts.filter((post) => {
        return [post.title, post.summary, post.category].some((value) => String(value || '').toLowerCase().includes(query))
      })
    },
    totalPostCount() {
      return this.filteredRawPosts.length
    },
    totalPages() {
      return Math.max(Math.ceil(this.totalPostCount / this.pageSize), 1)
    },
    currentPageSourcePosts() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredRawPosts.slice(start, start + this.pageSize)
    },
    lastUpdatedLabel() {
      return this.lastUpdatedAt ? this.formatTime(this.lastUpdatedAt) : ''
    },
    activeLanguageLabel() {
      return this.languages.find((language) => language.value === this.activeLanguage)?.label || 'EN'
    },
    translationProviderLabel() {
      if (this.activeLanguage === 'en') return 'No translate'
      if (this.translationInProgress) return 'In process...'
      return this.formatTranslationProvider(this.translationProvider)
    },
  },
  async mounted() {
    document.addEventListener('click', this.closeLanguageMenu)
    window.addEventListener('rio:translation-provider', this.updateTranslationProvider)
    this.syncFeedQuery()
    await this.loadPosts()
    this.restoreReturnPosition()
  },
  beforeRouteLeave(to) {
    if (to.name === 'news-article') this.saveReturnPosition()
    else sessionStorage.removeItem(NEWS_RETURN_STATE_KEY)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeLanguageMenu)
    window.removeEventListener('rio:translation-provider', this.updateTranslationProvider)
  },
  methods: {
    updateTranslationProvider(event) {
      this.translationProvider = event.detail || null
    },
    formatTranslationProvider(provider) {
      if (provider?.status === 'limit') return 'Limit reached'

      const label = provider?.label || 'Unknown provider'
      const queue = provider?.index && provider?.total ? ` - ${provider.index}/${provider.total}` : ''
      const cache = provider?.cached ? 'Cache - ' : ''
      return `${cache}${label}${queue}`
    },
    finalizeTranslationProvider() {
      if (this.activeLanguage === 'en') return

      const provider = getLastTranslationProvider()
      this.translationProvider = provider?.status && provider.status !== 'none'
        ? provider
        : {
          key: 'feed-cache',
          label: 'Feed cache',
          cached: true,
          status: 'cached',
        }
    },
    closeLanguageMenu() {
      this.languageMenuOpen = false
    },
    selectLanguage(language) {
      this.activeLanguage = language
      this.languageMenuOpen = false
    },
    selectFeed(feedKey) {
      if (this.activeFeedKey === feedKey || this.loading) return
      this.scrollToTop()
      this.activeFeedKey = feedKey
      this.search = ''
      this.currentPage = 1
      this.persistNewsState()
      this.syncFeedQuery()
      this.loadPosts()
    },
    selectSource(source) {
      const feedKey = source === 'blue'
        ? 'blue'
        : source === 'noob-club'
          ? 'noob-club'
          : this.activeSource === 'wowhead'
            ? this.wowheadCategory
            : 'all'

      this.selectFeed(feedKey)
    },
    syncFeedQuery() {
      const { feed, source, category, ...otherQuery } = this.$route.query
      const feedQuery = this.activeSource === 'wowhead'
        ? { source: 'wowhead', category: this.wowheadCategory }
        : { source: this.activeSource }

      const hasCanonicalQuery = !feed
        && source === feedQuery.source
        && (feedQuery.category ? category === feedQuery.category : !category)
      if (hasCanonicalQuery) return

      this.$router.replace({
        name: 'news',
        query: {
          ...otherQuery,
          ...feedQuery,
        },
      })
    },
    changePage(page) {
      if (this.loading || page === this.currentPage) return
      this.scrollToTop()
      this.currentPage = page
      this.loadCurrentPage()
    },
    currentScrollTop() {
      return Math.max(
        Number(window.scrollY) || 0,
        Number(document.documentElement?.scrollTop) || 0,
        Number(document.querySelector('.app-global-wrapper')?.scrollTop) || 0,
      )
    },
    scrollToPosition(top) {
      const position = Math.max(0, Number(top) || 0)
      window.scrollTo({ top: position, behavior: 'auto' })
      document.querySelector('.app-global-wrapper')?.scrollTo({ top: position, behavior: 'auto' })
    },
    scrollToTop() {
      this.scrollToPosition(0)
    },
    saveReturnPosition() {
      try {
        sessionStorage.setItem(NEWS_RETURN_STATE_KEY, JSON.stringify({
          activeFeedKey: this.activeFeedKey,
          currentPage: this.currentPage,
          search: this.search,
          scrollTop: this.currentScrollTop(),
        }))
      } catch (error) {
        console.error('Unable to save News return position', error)
      }
    },
    restoreReturnPosition() {
      if (this.pendingScrollTop === null) return

      const position = this.pendingScrollTop
      this.pendingScrollTop = null
      sessionStorage.removeItem(NEWS_RETURN_STATE_KEY)

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.scrollToPosition(position))
        })
      })
    },
    articleRoute(post) {
      return {
        name: 'news-article',
        query: {
          url: post.link,
          source: post.source || 'wowhead',
        },
      }
    },
    async loadPosts(force = false) {
      this.loading = true
      this.loadError = ''
      const feedLoadingId = await this.$store.dispatch('startGlobalLoading', `Loading ${this.activeFeed.label}...`)

      try {
        this.rawPosts = await getWowheadFeedPostsRaw(this.activeFeedKey, { force })
        this.currentPage = Math.min(this.currentPage, this.totalPages)
        await this.loadCurrentPage(false)
        const fallback = getFeedFallbackStatus(this.activeFeedKey)
        this.lastUpdatedAt = fallback?.timestamp
          ? new Date(fallback.timestamp).toISOString()
          : new Date().toISOString()

        if (fallback) {
          this.$store.dispatch('notify', {
            type: 'warning',
            title: `Saved ${this.activeFeed.label} feed`,
            description: 'The live news request failed, so the most recent saved posts are shown.',
          })
        }
      } catch (error) {
        this.loadError = `${this.activeFeed.label} is unavailable right now. Try refreshing the news or opening the source directly.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: `${this.activeFeed.label} unavailable`,
          description: this.loadError,
        })
      } finally {
        this.finalizeTranslationProvider()
        this.$store.dispatch('finishGlobalLoading', feedLoadingId)
        this.loading = false
      }
    },
    async loadCurrentPage(ownLoading = true) {
      if (ownLoading) {
        this.loading = true
        this.loadError = ''
      }

      const pagePosts = this.currentPageSourcePosts
      let posts = pagePosts
      let translationLoadingId = null

      try {
        if (!this.isNativeRussianFeed && this.activeLanguage !== 'en' && pagePosts.length) {
          translationLoadingId = await this.$store.dispatch('startGlobalLoading', `Translating ${this.activeFeed.label} page ${this.currentPage} to ${this.activeLanguageLabel}...`)
          this.translationInProgress = true
          posts = await translatePosts(pagePosts, this.activeLanguage)
          this.finalizeTranslationProvider()
        }

        this.posts = this.activeFeed.kind === 'blue' ? posts : await this.enrichNewsBanners(posts)
      } catch (error) {
        this.loadError = 'Unable to prepare this news page right now. Try refreshing the feed.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: `${this.activeFeed.label} page unavailable`,
          description: this.loadError,
        })
      } finally {
        if (translationLoadingId) {
          this.$store.dispatch('finishGlobalLoading', translationLoadingId)
        }
        this.translationInProgress = false
        if (ownLoading) this.loading = false
      }
    },
    async enrichNewsBanners(posts) {
      const bannerLimit = 12
      const enrichedPosts = [...posts]
      const targets = enrichedPosts.filter((post) => ['news', 'noob'].includes(post.kind)).slice(0, bannerLimit)
      const results = await Promise.allSettled(
        targets.map(async (post) => {
          if (post.imageUrl || !post.link) return post
          return {
            ...post,
            imageUrl: await getWowheadArticleImage(post.link, post.source),
          }
        })
      )

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const postIndex = enrichedPosts.findIndex((post) => post.id === result.value.id)
          if (postIndex !== -1) {
            enrichedPosts[postIndex] = result.value
          }
        }
      })

      return enrichedPosts
    },
    handleCardImageError(post) {
      post.imageUrl = ''
    },
    formatDate(value) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
    formatTime(value) {
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
    persistNewsState() {
      try {
        localStorage.setItem(NEWS_STATE_KEY, JSON.stringify({
          viewMode: this.viewMode,
          activeFeedKey: this.activeFeedKey,
        }))
        localStorage.setItem(NEWS_LANGUAGE_KEY, this.activeLanguage)
      } catch (error) {
        console.error('Unable to save News state', error)
      }
    },
  },
  watch: {
    '$route.fullPath'() {
      const requestedFeedKey = resolveRouteFeedKey(this.$route.query)
      if (!requestedFeedKey || requestedFeedKey === this.activeFeedKey) return

      this.scrollToTop()
      this.activeFeedKey = requestedFeedKey
      this.search = ''
      this.currentPage = 1
      this.persistNewsState()
      this.loadPosts()
    },
    viewMode() {
      this.persistNewsState()
    },
    activeLanguage() {
      this.persistNewsState()
      this.loadCurrentPage()
    },
    search() {
      this.currentPage = 1
      this.loadCurrentPage()
    },
  },
}
</script>
